import {Box, Button, styled} from "@mui/material";
import {buildPath, mapEventType, scale, timeStringToDayJs} from "../../../utils";
import Text from "../../text";
import LABEL from "../../../resources/label";
import theme, {FONTS} from "../../../constants/theme";
import ThemedSelect from "../../input/select";
import React, {useEffect, useState} from "react";
import ThemedTextInput from "../../input/text";
import ThemedText from "../../text";
import {SelectValue, TemplateResponse} from "../../../types";
import {fetchTemplates} from "../../../features/operator";
import DateSelector from "../../date-selector";
import TimeRangePicker from "../../input/time-range";
import dayjs, {Dayjs} from "dayjs";
import {useNavigate} from "react-router-dom";
import FullScreenDialog from "../../full-screen-dialog";


const Wrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  height: scale(409 + 59 + 52 + 50),
  width: scale(630),
  marginTop: scale(30),
  rowGap: scale(25),
});

const EventType = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  rowGap: scale(18),
  height: scale(29 + 20.14 + 40 + 18),
});

const EventData = styled(Box)({
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: 'repeat(2, 1fr)',
  rowGap: scale(18),
  columnGap: scale(30.2),
  width: '100%',
  height: scale(29 + 20.14 + 40 + 18 + 20.14 + 40 + 20),
});

const EventHour = styled(Box)({
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: 'repeat(2, 1fr)',
  rowGap: scale(8),
  columnGap: scale(30.2),
  width: '100%',
  height: scale(29 + 20.14 + 40 + 18),
});

const ButtonGroup = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  columnGap: scale(11),
  marginTop: scale(10),

});

const ThemedButton = styled(Button)({
  borderRadius: scale(30),
  width: scale(160),
  height: scale(52),
  marginTop: scale(13),
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.lightGray.main,
    cursor: 'not-allowed',
  },
});

const NewDonationForm = () => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateList, setTemplateList] = useState<TemplateResponse[]>([]);
  const [disabledInput, setDisabledInput] = useState<boolean>(false);
  const [isTemplateSelected, setIsTemplateSelected] = useState<boolean>(false);
  const [eventName, setEventName] = useState<string>('')
  const [eventNameError, setEventNameError] = useState<boolean>(false)
  const [address, setAddress] = useState<string>('')
  const [postazioni, setPostazioni] = useState<number | null>(null)
  const [date, setDate] = useState<Dayjs | null>(null)
  const [amTurn, setAmTurn] = useState<Dayjs[] | null>(null)
  const [pmTurn, setPmTurn] = useState<Dayjs[] | null>(null)
  const [selectedValue, setSelectedValue] = useState<SelectValue | null>(null)
  const [eventType, setEventType] = useState<'sangue' | 'plasma' | 'plasma & sangue'>('sangue')

  const genericEvents: SelectValue[] = [{
    value: '0',
    name: 'Raccolta sangue (generica)'
  }, {
    value: '1',
    name: 'Raccolta plasma (generica)'
  }]

  const setSelectedStockEvent = (selValue: SelectValue) => {
    setSelectedValue(selValue)
    const value = selValue.name
    if (value !== genericEvents[0].name && value !== genericEvents[1].name && value !== '') {
      const template = templateList.find(el => el.name === value)
      if (template) {
        setIsTemplateSelected(true)
        setEventName(template.name)
        setAddress(template.address)
        setEventType(template.type)
        let array: Dayjs[] = [timeStringToDayJs(template.am_start), timeStringToDayJs(template.am_end)]
        setAmTurn(array)
        array = [timeStringToDayJs(template.pm_start), timeStringToDayJs(template.pm_end)]
        setPmTurn(array)
        setDisabledInput(true)
        setPostazioni(null)
      }
    } else {
      const baseEvent = genericEvents.find(el => el.name === value)
      if (baseEvent) {
        setDisabledInput(false)
        setIsTemplateSelected(false)
        setEventType(baseEvent.value as 'sangue' | 'plasma')

        setEventName('')
        setAddress('')
        setPostazioni(null)
        setAmTurn(null)
        setPmTurn(null)
        setDate(null)
      }
    }
  }

  const saveFormToSession = () => {
    const validAmTurn = amTurn && amTurn.length === 2 && amTurn[0]?.isValid() && amTurn[1]?.isValid()
      ? amTurn.map(d => d.format('HH:mm'))
      : null;

    const validPmTurn = pmTurn && pmTurn.length === 2 && pmTurn[0]?.isValid() && pmTurn[1]?.isValid()
      ? pmTurn.map(d => d.format('HH:mm'))
      : null;
    const dataToSave = {
      isTemplateSelected,
      selectedValue: selectedValue,
      eventName,
      address,
      postazioni,
      date: date?.toISOString() ?? null,
      amTurn: validAmTurn,
      pmTurn: validPmTurn,
      eventType: mapEventType(eventType),
    };

    sessionStorage.setItem('createDonation', JSON.stringify(dataToSave));
    navigate(buildPath('/operatore/conferma-crea-raccolta'));
  }

  const isTurnValid = (turn: Dayjs[] | null) =>
    turn !== null &&
    Array.isArray(turn) &&
    turn.length === 2 &&
    turn[0]?.isValid() &&
    turn[1]?.isValid();

  const isButtonDisabled = () => {
    const hasTurn = isTurnValid(amTurn) || isTurnValid(pmTurn);
    const seatsNeeded = !isTemplateSelected && (Number.isNaN(postazioni) || postazioni === null || postazioni === 0)
    return (!eventName.trim() || eventNameError || date === null || !address.trim() || seatsNeeded || !hasTurn)
  }

  useEffect(() => {
    fetchTemplates().then(res => {
      setTemplateList(res)
    })
    const storedData = sessionStorage.getItem('createDonation');
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);

        setIsTemplateSelected(parsed.isTemplateSelected ?? false);
        setSelectedValue(parsed.selectedValue)
        setDisabledInput(parsed.isTemplateSelected)
        setEventName(parsed.eventName ?? '');
        setAddress(parsed.address ?? '');
        setPostazioni(parsed.postazioni ?? null);
        setDate(parsed.date ? dayjs(parsed.date) : null);
        setAmTurn(
          Array.isArray(parsed.amTurn) && parsed.amTurn.length === 2
            ? parsed.amTurn.map((t: string) => dayjs(t, 'HH:mm'))
            : null
        );
        setPmTurn(
          parsed.pmTurn && parsed.pmTurn.length === 2
            ? parsed.pmTurn.map((t: string) => dayjs(t, 'HH:mm'))
            : null
        );
        setEventType(parsed.eventType ?? 'sangue');
      } catch (error) {
        console.error('Errore nel parsing dei dati salvati:', error);
      }
    }
    setIsLoading(false)
  }, [])

  if (isLoading) {
    return null;
  }

  const options = [
    ...genericEvents,
    ...templateList.map((template, index) => ({
      value: (genericEvents.length + index).toString(),
      name: template.name
    }))]

  const safeSelectedValue = options.find(o => o.value === selectedValue?.value) ?? genericEvents[0];

  return (
    <>
      <FullScreenDialog
        title={'VUOI ABBANDONARE LA CREAZIONE DI UNA NUOVA RACCOLTA?'}
        content={'Se prosegui perderai tutti i dati inseriti fino a questo punto'}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={() => {
          sessionStorage.removeItem('createDonation');
          navigate(buildPath('/operatore/calendario'))
        }}
        colorStyle={'blue'}
        cancelText={'ANNULLA'}
        confirmText={'ABBANDONA'}
      />
      <Wrapper>
        <EventType>
          <Text
            text={LABEL.EVENT}
            fontFamily={FONTS.MONTSERRAT}
            color={theme.palette.text.secondary}
            fontSize={scale(24)}
            fontWeight={'bold'}
            sx={{textTransform: 'uppercase', alignSelf: 'center'}}/>
          <ThemedSelect
            variant={'filled'}
            label={'Tipologia'}
            id={'tipologia'}
            values={options}
            selectedValue={safeSelectedValue}
            setSelectedValue={(value) => setSelectedStockEvent(value)}
            sxProp={{
              minWidth: scale(120),
              width: scale(299.8),
              alignSelf: 'flex-start',
            }}/>
        </EventType>
        <EventData>
          <Text
            text={LABEL.EVENT_DATA}
            fontFamily={FONTS.MONTSERRAT}
            color={theme.palette.text.secondary}
            fontSize={scale(24)}
            fontWeight={'bold'}
            sx={{
              textTransform: 'uppercase',
              alignSelf: 'center',
              justifySelf: 'center',
              gridColumn: '1 / 3',
              gridRow: '1 / 3',
            }}/>
          <ThemedTextInput
            htmlFor={'event-name'}
            label={'Nome evento'}
            placeholder={"Inserisci il nome dell'evento"}
            errorLabel={''}
            disabled={disabledInput}
            value={eventName}
            setValue={(value) => setEventName(value)}
            setErrorValue={setEventNameError}
            required={true}
          />
          <DateSelector value={date} setValue={setDate}/>
          <ThemedTextInput
            htmlFor={'event-name'}
            label={'Indirizzo'}
            placeholder={"Inserisci l'indirizzo"}
            errorLabel={''}
            disabled={disabledInput}
            value={address}
            setValue={(value) => setAddress(value)}
            setErrorValue={setEventNameError}
            required={true}
          />
          <ThemedTextInput
            htmlFor={'event-name'}
            label={'postazioni'}
            placeholder={"Inserisci il numero di postazioni"}
            disabled={disabledInput}
            value={isTemplateSelected ? '-' : postazioni !== null ? String(postazioni) : '0'}
            errorLabel={''}
            validate={value => {
              return Number(value) > 0
            }}
            setValue={(value) => {
              const numericValue = Number(value);
              setPostazioni(isNaN(numericValue) ? null : numericValue);
            }}
            setErrorValue={setEventNameError}
            required={true}
          />
        </EventData>
        <EventHour>
          <Text
            text={LABEL.EVENT_HOURS}
            fontFamily={FONTS.MONTSERRAT}
            color={theme.palette.text.secondary}
            fontSize={scale(24)}
            fontWeight={'bold'}
            sx={{
              textTransform: 'uppercase',
              alignSelf: 'center',
              justifySelf: 'center',
              gridColumn: '1 / 3',
              gridRow: '1 / 3',
            }}/>
          <TimeRangePicker
            label={'Turno mattina'}
            am={true}
            disabled={disabledInput}
            values={amTurn}
            setValues={(values) => setAmTurn(values)}/>
          <TimeRangePicker
            label={'Turno pomeriggio'}
            am={false}
            disabled={disabledInput}
            values={pmTurn}
            setValues={(values) => setPmTurn(values)}/>
        </EventHour>
        <ButtonGroup>
          <ThemedButton variant="contained" onClick={() => setDialogOpen(true)}>
            <ThemedText text={'annulla'} fontWeight={600} fontSize={scale(18)} fontFamily={FONTS.MONTSERRAT}/>
          </ThemedButton>
          <ThemedButton
            variant="contained"
            disabled={isButtonDisabled()}
            onClick={() => {
              saveFormToSession();
            }}
          >
            <ThemedText text={'prosegui'} fontWeight={600} fontSize={scale(18)} fontFamily={FONTS.MONTSERRAT}/>
          </ThemedButton>
        </ButtonGroup>
      </Wrapper>
    </>
  );
}

export default NewDonationForm;
